import {getSizeCommerceView} from '../../../utils/onecStockAvailability';
import {isServerSnapshotTimeValid} from '../../../utils/serverSnapshotTime';

const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;

// A card may start a draft. The server will independently re-read every SKU
// and the latest complete 1C snapshot before quoting or accepting an order.
export function canDraftAutomaticPreorder(item, sizeName, count = 1) {
  const view = getSizeCommerceView(item, sizeName, count), row = view.descriptor;
  const confirmedZero = row?.reasonCode === 'automatic_zero_preorder' &&
    row.stock?.state === 'confirmed' && row.stock.quantity === 0 &&
    Date.parse(row.stock.validUntil) >= Date.now();
  const confirmedLastUnit = row?.reasonCode === 'automatic_last_unit_preorder' &&
    row.stock?.state === 'confirmed' && row.stock.quantity === 1 &&
    Date.parse(row.stock.validUntil) >= Date.now();
  const absentFromFull = row?.reasonCode === 'automatic_source_absent_preorder' &&
    row.sourceMatch === 'absent_in_applied_full' && row.stock?.state === 'unknown' &&
    row.stock.quantity === null && row.stock.validUntil === null;
  const unconfirmed = row?.reasonCode === 'automatic_unconfirmed_preorder' &&
    ((['unknown', 'stale'].includes(row.stock?.state) && row.stock.quantity === null &&
      row.stock.validUntil === null) || (row.stock?.state === 'confirmed' && row.stock.quantity === 0 &&
      Date.parse(row.stock.validUntil) >= Date.now()));
  return Boolean(process.env.REACT_APP_AUTOMATIC_PREORDER_SITE_CHECKOUT_ENABLED === 'true' &&
    item?.is_active === true && item.product?.active_product === true &&
    item.product?.electron_sertificate !== true && id(item.id) && id(item.productId) && id(item.colorId) &&
    id(row?.sizeId) && view.mode === 'enforced' && row.kind === 'ordinary' &&
    row.action === 'preorder' && row.checkoutAllowed === true &&
    (confirmedZero || confirmedLastUnit || absentFromFull || unconfirmed) && Number.isSafeInteger(count) && count > 0 && count <= 999 &&
    isServerSnapshotTimeValid(item.commerce.evaluatedAt, Date.now(), 5 * 60 * 1000));
}

export function automaticPreorderItems(cart) {
  if (!cart?.session || !Array.isArray(cart.lines) || !cart.lines.length || cart.lines.length > 100) return null;
  const items = [];
  for (const line of cart.lines) {
    const item = line?.item, name = line?.currentSize;
    if (line?.verification !== 'verified' || line.transferKey || !canDraftAutomaticPreorder(item, name, line.quantity) ||
      line.identity?.childProductId !== item.id || line.identity?.colorId !== item.colorId ||
      line.identity?.sizeId !== getSizeCommerceView(item, name).descriptor?.sizeId) return null;
    items.push({child_product_id: item.id, productId: item.productId, colorId: item.colorId,
      sizeId: line.identity.sizeId, currentSize: name, count: line.quantity});
  }
  return items;
}

// The ordinary delivery controls may be shared, but its basket, coupon and
// bonuses are not part of this detached order. Only current SKU identities
// travel to the preorder quote; the server owns price and delivery cost.
export function automaticPreorderCalculation(cart, delivery, payment) {
  const items = automaticPreorderItems(cart), deliveryId = delivery?.active?.id, paymentId = payment?.id;
  if (!items || !id(deliveryId) || !id(paymentId)) return null;
  const point = delivery.officePvz;
  const selected = delivery.selectedAddress;
  const address = point ? delivery.cityInfo : selected ? selected.addressFields?.all ?? selected.all : delivery.customerInfo?.all;
  const data = address?.data;
  const location = [data?.fias_id, data?.geoname_id, data?.kladr_id].find(value => typeof value === 'string' &&
    value.trim().length > 0 && value.length <= 255);
  const shippingContext = {};
  if (location) shippingContext.addressId = location;
  if (point) {
    const pointId = point.code ?? point.id;
    if (!((typeof pointId === 'string' && pointId.length > 0 && pointId.length <= 255) || id(pointId))) return null;
    shippingContext.pointId = String(pointId);
  }
  if (!location && typeof address?.value === 'string' && address.value.trim() && address.value.length <= 300) {
    shippingContext.addressQuery = address.value;
  }
  if (!shippingContext.addressId && !shippingContext.addressQuery) return null;
  return {items, delivery: deliveryId, payment: paymentId, promo: null, bonusesAmount: 0, shippingContext};
}

const cents = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 &&
  Number.isSafeInteger(Math.round(value * 100)) && Math.abs(Math.round(value * 100) / 100 - value) < 1e-9;

export function readAutomaticPreorderQuote(raw, calculation, scope) {
  const pricing = raw?.pricing, expected = calculation?.items;
  if (raw?.schemaVersion !== 'site-order-quote-v1' || !/^[a-f0-9]{64}$/.test(raw.conditionsHash) ||
    !pricing || pricing.schemaVersion !== 'site-basket-pricing-v1' || pricing.scope !== scope ||
    pricing.basketId !== null || pricing.basketRevision !== null || pricing.selectionState !== 'selected' ||
    pricing.deliveryState !== 'selected' || !Array.isArray(expected) || !expected.length ||
    !Array.isArray(pricing.lines) || pricing.lines.length !== expected.length ||
    !['totalPrice', 'merchandisePrice', 'deliveryPrice', 'priceNoSell', 'promoDiscountSumm'].every(key => cents(pricing[key]))) return null;
  let merchandise = 0, base = 0;
  for (let index = 0; index < expected.length; index += 1) {
    const item = expected[index], line = pricing.lines[index];
    const key = `sku:${item.child_product_id}:${encodeURIComponent(item.currentSize)}:${index}`;
    if (line?.lineKey !== key || line.count !== item.count ||
      !['basePrice', 'unitPrice', 'lineTotal', 'discount'].every(field => cents(line[field])) ||
      Math.round(line.basePrice * 100) * item.count - Math.round(line.lineTotal * 100) !== Math.round(line.discount * 100)) return null;
    merchandise += Math.round(line.lineTotal * 100);
    base += Math.round(line.basePrice * 100) * item.count;
  }
  if (!Number.isSafeInteger(merchandise) || !Number.isSafeInteger(base) ||
    merchandise !== Math.round(pricing.merchandisePrice * 100) ||
    base !== Math.round(pricing.priceNoSell * 100) ||
    merchandise + Math.round(pricing.deliveryPrice * 100) !== Math.round(pricing.totalPrice * 100)) return null;
  return {conditionsHash: raw.conditionsHash, pricing, calculation};
}
